/* ============================================================
   SAM-1074 — the Activity page's data layer (contract of Logic's PR #738, sam-lambda-functions):
   - GET /bookings/stats?business_id=&from=&to=[&status=]   (Bearer; business_id is required)
   - GET /business_access/stats?date_from=&date_to=[&business_id=&entity=&status=&q=]   (like /combined)
   - GET /bookings?business_id=&from=&to=&per_page=   (rows: data.items, newest first)
   Dates are YYYY-MM-DD, both ends included, at most 366 days. Both stats answer
   {from, to, timezone, total, by_status:[{status,count}], by_day:[{date,total,by_status:{status:count}}]}
   with every day of the window (zeros included) and lower-case status names; passes count on the day they
   start. The page never reads the endpoints directly: it goes through these functions, so a change in
   the contract stays in this file.
   ============================================================ */
import { directApiInstance } from './httpClient';
import { businessAccessApi } from './businessAccessClient';

export interface StatsDay {
  date: string;
  total: number;
  by_status: Record<string, number>;
}

export interface StatsResponse {
  from: string;
  to: string;
  timezone?: string;
  total: number;
  by_status: Array<{ status: string; count: number }>;
  by_day: StatsDay[];
  /** Passes only, when the count mixes business passes and residential invitations. */
  by_entity?: Record<string, number>;
  /** Without business_id: each business in the window. Bookings give `count`; passes give `total` and `by_status`
   *  (read here as `count` too, with `by_status` when the answer has it). */
  by_business?: Array<{ business_id: string; name: string; count: number; by_status?: Record<string, number> }>;
}

/** "No Show" / "no show" / "no_show" → "no_show": the one key a status is compared by. */
export const statusKey = (status: string): string => String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/** The stats body from an axios response or the already-unwrapped body (`{data:{…}}` or the shape itself). */
export function readStats(raw: unknown): StatsResponse {
  const r = raw as { data?: unknown } & Partial<StatsResponse>;
  const body = (r && Array.isArray((r as Partial<StatsResponse>).by_day) ? r : (r?.data as Partial<StatsResponse> | undefined)) ?? {};
  return {
    from: body.from ?? '', to: body.to ?? '', timezone: body.timezone,
    total: Number(body.total ?? 0),
    by_status: Array.isArray(body.by_status) ? body.by_status.map((s) => ({ status: statusKey(s.status), count: Number(s.count) || 0 })) : [],
    by_day: Array.isArray(body.by_day)
      ? body.by_day.map((d) => ({
        date: String(d.date), total: Number(d.total) || 0,
        by_status: Object.fromEntries(Object.entries(d.by_status || {}).map(([k, v]) => [statusKey(k), Number(v) || 0])),
      }))
      : [],
    ...(body.by_entity ? { by_entity: body.by_entity } : {}),
    ...(Array.isArray(body.by_business)
      ? {
        by_business: body.by_business.map((b) => {
          const row = b as { business_id: unknown; name?: unknown; count?: unknown; total?: unknown; by_status?: Record<string, unknown> };
          return {
            business_id: String(row.business_id), name: String(row.name ?? ''), count: Number(row.count ?? row.total) || 0,
            ...(row.by_status ? { by_status: Object.fromEntries(Object.entries(row.by_status).map(([k, v]) => [statusKey(k), Number(v) || 0])) } : {}),
          };
        }),
      }
      : {}),
  };
}

/** `business_id` is optional: without it a tenant admin gets the whole tenant, with `by_business` (others get 403). */
export interface BookingStatsQuery { business_id?: string; from: string; to: string; status?: string }

export async function fetchBookingStats(q: BookingStatsQuery): Promise<StatsResponse> {
  const params: Record<string, string> = { from: q.from, to: q.to };
  if (q.business_id) params.business_id = q.business_id;
  if (q.status) params.status = q.status;
  return readStats(await directApiInstance({ url: '/bookings/stats', method: 'GET', params }));
}

/** `estate_group_id` (SAM-942, lambdas #841) narrows the rows to one estate group of the caller's tenant. */
export interface PassStatsQuery { business_id?: string; date_from: string; date_to: string; entity?: 'business_access' | 'invitation'; status?: string; estate_group_id?: string }

export async function fetchPassStats(q: PassStatsQuery): Promise<StatsResponse> {
  const params: Record<string, string> = { date_from: q.date_from, date_to: q.date_to };
  if (q.business_id) params.business_id = q.business_id;
  if (q.entity) params.entity = q.entity;
  if (q.status) params.status = q.status;
  if (q.estate_group_id) params.estate_group_id = q.estate_group_id;
  return readStats(await businessAccessApi({ url: 'stats', method: 'GET', params }));
}

/** The few fields of a booking row the Activity page needs (GET /bookings, `data.items`). */
export interface UpcomingBooking {
  id: string;
  /** Local wall-clock start (the API stores Dominican Republic time). */
  start: Date;
  status: string;
}

const parseWallClock = (raw: unknown): Date | null => {
  const d = new Date(String(raw ?? '').replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
};

export function readBookingRows(raw: unknown): UpcomingBooking[] {
  const r = raw as { data?: { items?: unknown[] } | unknown[]; items?: unknown[] };
  const list = (Array.isArray(r?.data) ? r.data : r?.data?.items ?? r?.items ?? []) as Array<Record<string, unknown>>;
  return list.flatMap((row) => {
    const start = parseWallClock(row.reservation_start);
    return start ? [{ id: String(row.id), start, status: statusKey(String(row.status_name ?? '')) }] : [];
  });
}

export interface BookingRowsQuery { business_id: string; from: string; to: string; per_page?: number }

/** The earliest pending and confirmed bookings of a business in the window (GET /bookings order=asc, status=a,b). */
export async function fetchUpcomingBookings(q: BookingRowsQuery): Promise<UpcomingBooking[]> {
  return readBookingRows(await directApiInstance({
    url: '/bookings', method: 'GET',
    params: { business_id: q.business_id, from: q.from, to: q.to, status: 'pending,confirmed', order: 'asc', per_page: q.per_page ?? 100 },
  }));
}
